import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";

import { allSlotSeriesSlicesSelector } from "../atoms/hangingProtocolAtoms";
import { contourVersionAtom, segmentationVolumeIdAtom } from "../atoms/segmentationAtoms";
import type { ResolvedPredictionMask } from "../predictions/segmentationMask";
import { emit } from "../lib/viewerBus";
import {
  getCornerstone,
  getCornerstoneTools,
  loadImagesWithConcurrency,
} from "../utils/dicomLoader";
import { logger } from "../utils/logger";
import {
  addPredictionMaskContours,
  capturePredictionMaskEditSnapshot,
  createPredictionMaskOverlayState,
  detachPredictionMaskSegment,
  predictionMaskContoursArePresent,
  predictionMaskEditChanged,
  predictionSliceKey,
  removePredictionMaskContours,
  restorePredictionMaskEditSnapshot,
  type PredictionMaskEditSnapshot,
  type PredictionMaskOverlayPass,
} from "../utils/predictionMaskOverlay";

/**
 * Whether a segment's outline is on screen yet (ENG-269). Segments missing
 * from the map need no wait: hidden, handed to the editor, or on a series
 * no viewport shows. "failed" may clear on a reload (the files didn't load in
 * time); "unavailable" won't (the files don't match the mask, or the series
 * has no segmentation volume).
 */
export type PredictionMaskLoadStatus = "loading" | "ready" | "failed" | "unavailable";

/** Why a segment's outline isn't drawn; logged once per segment and cause. */
type PredictionMaskFailureCause =
  | "header-timeout"
  | "contour-error"
  | "no-segmentation-volume"
  | "sop-mismatch"
  | "unmatched-s3-key";

const READY_POLL_ATTEMPTS = 50;
/** A segment's outline gets this long to draw before its card stops waiting. */
export const MASK_LOADING_TIMEOUT_MS = 30_000;
const PREFETCH_CONCURRENCY = 4;
const PREFETCH_MAX_ATTEMPTS = 3;
const PREFETCH_RETRY_MS = 2000;

export interface PredictionEditCompletion {
  cancel: () => boolean;
  commit: () => boolean;
  /** Live geometry differs from the edit-entry checkpoint. Throws when it cannot tell. */
  isModified: () => boolean;
}

/** Render engine-emitted masks as locked contours on every visible native series. */
export function usePredictionMaskOverlay(
  mask: ResolvedPredictionMask | null,
  studyId: string,
  hiddenSegmentIndices: readonly number[] = [],
  releasedSegmentIndices: readonly number[] = [],
): {
  beginPredictionEdit: (
    predictionSegmentIndex: number,
    savedTarget?: { annotationId: string; segmentIndex: number },
  ) => boolean;
  getPredictionEditCompletion: (segmentIndex: number) => PredictionEditCompletion | null;
  /** Per viewer-local segment index; "loading" until the segmentation volume exists. */
  maskLoadStatus: Readonly<Record<number, PredictionMaskLoadStatus>>;
  /** Epoch ms at which each "loading" segment stops waiting (its card's countdown). */
  maskLoadDeadlines: Readonly<Record<number, number>>;
} {
  const store = useStore();
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const bumpContourVersion = useSetAtom(contourVersionAtom);
  const visibleSlices = useAtomValue(allSlotSeriesSlicesSelector);
  const visibleSeriesKey = useMemo(
    () => [...new Set(visibleSlices.map((slice) => slice.seriesId))].sort().join("+"),
    [visibleSlices],
  );
  const stateRef = useRef(createPredictionMaskOverlayState());
  const activeStudyRef = useRef<string | null>(studyId);
  const editSnapshotRef = useRef<{
    studyId: string;
    savedAnnotationId?: string;
    snapshot: PredictionMaskEditSnapshot;
  } | null>(null);
  const hiddenSegmentsKey = [...hiddenSegmentIndices].sort((a, b) => a - b).join(",");
  const hiddenSegments = useMemo(
    () => new Set(hiddenSegmentIndices),
    // The sorted key is the semantic dependency; callers need not memoize the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hiddenSegmentsKey],
  );
  const appliedHiddenSegmentsKey = useRef<string | null>(null);
  const releasedSegmentsKey = [...releasedSegmentIndices].sort((a, b) => a - b).join(",");
  const [{ maskLoadStatus, maskLoadDeadlines }, setMaskLoad] = useState<{
    maskLoadStatus: Readonly<Record<number, PredictionMaskLoadStatus>>;
    maskLoadDeadlines: Readonly<Record<number, number>>;
  }>({ maskLoadStatus: {}, maskLoadDeadlines: {} });
  const publishedStatusKey = useRef("");
  // Per study, so a verdict that re-runs the overlay effect doesn't restart
  // another card's wait or forget a cause that a reload won't fix.
  const loadingSinceRef = useRef(new Map<number, number>());
  const unmatchedSegmentsRef = useRef(new Set<number>());
  const loggedFailuresRef = useRef(new Set<string>());

  const beginPredictionEdit = useCallback(
    (
      predictionSegmentIndex: number,
      savedTarget?: { annotationId: string; segmentIndex: number },
    ): boolean => {
      const segmentIndex = savedTarget?.segmentIndex ?? predictionSegmentIndex;
      if (
        activeStudyRef.current !== studyId ||
        !segmentationVolumeId ||
        store.get(segmentationVolumeIdAtom) !== segmentationVolumeId ||
        !mask?.segments.some((segment) => segment.segmentIndex === predictionSegmentIndex) ||
        (!savedTarget && hiddenSegments.has(predictionSegmentIndex))
      ) {
        return false;
      }

      const current = editSnapshotRef.current;
      if (current) {
        return (
          current.studyId === studyId &&
          current.snapshot.segmentationVolumeId === segmentationVolumeId &&
          current.snapshot.segmentIndex === segmentIndex &&
          current.snapshot.predictionSegmentIndex === predictionSegmentIndex &&
          current.savedAnnotationId === savedTarget?.annotationId
        );
      }

      try {
        if (savedTarget) {
          const contours = getCornerstoneTools()
            .annotation.state.getAllAnnotations()
            .filter(
              (ann) =>
                ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
                (ann.data?.segmentation as { segmentIndex?: number } | undefined)?.segmentIndex ===
                  segmentIndex &&
                (ann.data?.contour?.polyline?.length ?? 0) >= 3,
            );
          // Capture/restore works by local index: reject mixed ownership at that index.
          if (
            !savedTarget.annotationId ||
            contours.length === 0 ||
            contours.some((ann) => ann.data?.savedAnnotationId !== savedTarget.annotationId)
          )
            return false;
        }
        const snapshot = capturePredictionMaskEditSnapshot(
          stateRef.current,
          segmentationVolumeId,
          segmentIndex,
          predictionSegmentIndex,
        );
        editSnapshotRef.current = {
          studyId,
          snapshot,
          savedAnnotationId: savedTarget?.annotationId,
        };
        detachPredictionMaskSegment(stateRef.current, predictionSegmentIndex);
        return true;
      } catch (error) {
        logger.warn("[predictionMask] edit checkpoint failed; ownership retained", error);
        return false;
      }
    },
    [mask, segmentationVolumeId, hiddenSegments, studyId, store],
  );

  // Obtain once after entry, before unlocking. Save must retain this completion
  // before its async request, rather than looking up a newer edit on response.
  const getPredictionEditCompletion = useCallback(
    (segmentIndex: number): PredictionEditCompletion | null => {
      const current = editSnapshotRef.current;
      if (
        !current ||
        current.studyId !== studyId ||
        current.snapshot.segmentationVolumeId !== segmentationVolumeId ||
        current.snapshot.predictionSegmentIndex !== segmentIndex
      ) {
        return null;
      }

      const isCurrent = () =>
        activeStudyRef.current === studyId &&
        editSnapshotRef.current === current &&
        store.get(segmentationVolumeIdAtom) === current.snapshot.segmentationVolumeId;

      if (!isCurrent()) return null;

      return {
        cancel: () => {
          if (!isCurrent()) {
            // Without this the editor just stays open with no explanation (ENG-277).
            logger.warn("[predictionMask] Cancel on a stale edit checkpoint");
            emit("toast", { message: "Could not cancel this edit. Please reopen the case." });
            return false;
          }
          try {
            restorePredictionMaskEditSnapshot(stateRef.current, current.snapshot);
          } catch (error) {
            logger.warn("[predictionMask] Cancel restore failed; checkpoint retained", error);
            emit("toast", {
              message: "Could not fully restore the previous annotation. Click Cancel to retry.",
              durationMs: 8000,
            });
            return false;
          }

          editSnapshotRef.current = null;
          // Refresh failure does not undo a successful geometry restoration.
          // Automatic refresh retry is deferred; report the failure to the user.
          let refreshFailed = false;
          try {
            bumpContourVersion((version) => version + 1);
          } catch (error) {
            refreshFailed = true;
            logger.warn("[predictionMask] restored contour notification failed", error);
          }
          try {
            getCornerstoneTools().segmentation.triggerSegmentationEvents.triggerSegmentationDataModified(
              current.snapshot.segmentationVolumeId,
              undefined,
              current.snapshot.segmentIndex,
            );
          } catch (error) {
            refreshFailed = true;
            logger.warn("[predictionMask] restored labelmap notification failed", error);
          }
          try {
            const engines = getCornerstone().getRenderingEngines() ?? [];
            if (engines.length === 0) refreshFailed = true;
            for (const engine of engines) {
              try {
                engine.render();
              } catch (error) {
                refreshFailed = true;
                logger.warn("[predictionMask] restored geometry render failed", error);
              }
            }
          } catch (error) {
            refreshFailed = true;
            logger.warn("[predictionMask] rendering engines unavailable", error);
          }
          if (refreshFailed) {
            emit("toast", {
              message: "Annotation restored, but the viewer could not refresh.",
              durationMs: 8000,
            });
          }
          return true;
        },
        isModified: () => {
          if (!isCurrent()) throw new Error("The prediction edit checkpoint is no longer current");
          return predictionMaskEditChanged(current.snapshot);
        },
        // Only after Save succeeds: preserve edited geometry and detachment.
        commit: () => {
          if (!isCurrent()) return false;
          editSnapshotRef.current = null;
          return true;
        },
      };
    },
    [studyId, segmentationVolumeId, store, bumpContourVersion],
  );

  useEffect(() => {
    activeStudyRef.current = studyId;
    return () => {
      activeStudyRef.current = null;
      removePredictionMaskContours(stateRef.current);
      // Local segment indices can be reused in the next study.
      stateRef.current.detachedSegmentIndices.clear();
      loadingSinceRef.current.clear();
      unmatchedSegmentsRef.current.clear();
      loggedFailuresRef.current.clear();
      editSnapshotRef.current = null;
    };
  }, [studyId]);

  // Verdict changes need a real contour reconciliation. Removing and
  // rebuilding the small immutable prediction overlay is deterministic across
  // every viewport and also allows Accept to restore a previously declined
  // segment without retaining stale hidden annotation ids.
  useEffect(() => {
    let released = false;
    for (const index of releasedSegmentsKey.split(",").filter(Boolean).map(Number)) {
      if (editSnapshotRef.current?.snapshot.predictionSegmentIndex === index) continue;
      released = stateRef.current.detachedSegmentIndices.delete(index) || released;
    }
    if (!released && appliedHiddenSegmentsKey.current === hiddenSegmentsKey) return;
    removePredictionMaskContours(stateRef.current);
    appliedHiddenSegmentsKey.current = hiddenSegmentsKey;
    bumpContourVersion((version) => version + 1);
  }, [hiddenSegmentsKey, releasedSegmentsKey, bumpContourVersion]);

  useEffect(() => {
    if (!mask) return;
    const state = stateRef.current;
    const visibleSeries = new Set(visibleSeriesKey.split("+").filter(Boolean));
    let cancelled = false;
    let attempts = 0;
    let lastPass: PredictionMaskOverlayPass | null = null;
    // Per image: loaded, tries so far, earliest next try, and whether one is in flight.
    const prefetchLoaded = new Set<string>();
    const prefetchAttempts = new Map<string, number>();
    const prefetchRetryAt = new Map<string, number>();
    const prefetchInFlight = new Set<string>();

    // Cards wait on this so a click never lands before the outline (ENG-269).
    // Returns whether any segment is still loading.
    const publish = (): boolean => {
      if (cancelled) return false;
      const now = Date.now();
      const loadingSince = loadingSinceRef.current;
      const status: Record<number, PredictionMaskLoadStatus> = {};
      const deadlines: Record<number, number> = {};
      const fail = (
        index: number,
        seriesId: string,
        result: "failed" | "unavailable",
        cause: PredictionMaskFailureCause,
      ) => {
        status[index] = result;
        const logKey = `${index}:${cause}`;
        if (loggedFailuresRef.current.has(logKey)) return;
        loggedFailuresRef.current.add(logKey);
        logger.error(
          `[predictionMask] segment ${index} outline ${result} (${cause}) on series ${seriesId}`,
        );
      };
      for (const segment of mask.segments) {
        const index = segment.segmentIndex;
        if (hiddenSegments.has(index) || state.detachedSegmentIndices.has(index)) continue;
        const shown = segment.slices.filter((slice) => visibleSeries.has(slice.seriesId));
        if (shown.length === 0) continue;
        const seriesId = shown[0].seriesId;
        // Drawn wins: an outline on screen is ready however its header arrived.
        if (shown.every((slice) => state.loadedKeys.has(predictionSliceKey(slice, index)))) {
          loadingSince.delete(index);
          status[index] = "ready";
          continue;
        }
        if (lastPass?.mismatchedSegmentIndices.has(index)) {
          fail(index, seriesId, "unavailable", "sop-mismatch");
          continue;
        }
        if (unmatchedSegmentsRef.current.has(index)) {
          fail(index, seriesId, "unavailable", "unmatched-s3-key");
          continue;
        }
        const since = loadingSince.get(index) ?? now;
        loadingSince.set(index, since);
        const waiting = lastPass?.pending.some((item) => item.segmentIndex === index) ?? true;
        if (!waiting && attempts >= READY_POLL_ATTEMPTS) {
          // Shown, but no viewer image holds its files; a reload finds the same files.
          unmatchedSegmentsRef.current.add(index);
          fail(index, seriesId, "unavailable", "unmatched-s3-key");
          continue;
        }
        const deadline = since + MASK_LOADING_TIMEOUT_MS;
        if (now < deadline) {
          status[index] = "loading";
          deadlines[index] = deadline;
        } else if (!segmentationVolumeId) {
          fail(index, seriesId, "unavailable", "no-segmentation-volume");
        } else {
          fail(index, seriesId, "failed", lastPass ? "header-timeout" : "contour-error");
        }
      }
      const key = JSON.stringify([status, deadlines]);
      if (key !== publishedStatusKey.current) {
        publishedStatusKey.current = key;
        setMaskLoad({ maskLoadStatus: status, maskLoadDeadlines: deadlines });
      }
      return Object.values(status).includes("loading");
    };

    // Stack viewports stream only the slice on screen, so fetch the waiting
    // slices; their headers then arrive without the reader scrolling there.
    // A failed fetch only queues a retry: the header can still arrive another
    // way (volume stream, scroll), so the card keeps waiting instead of failing.
    const prefetch = (items: PredictionMaskOverlayPass["pending"]) => {
      const now = Date.now();
      const fresh = [...new Set(items.map((item) => item.imageId))].filter(
        (imageId) =>
          !prefetchLoaded.has(imageId) &&
          !prefetchInFlight.has(imageId) &&
          (prefetchAttempts.get(imageId) ?? 0) < PREFETCH_MAX_ATTEMPTS &&
          now >= (prefetchRetryAt.get(imageId) ?? 0),
      );
      if (fresh.length === 0) return;
      for (const imageId of fresh) {
        prefetchInFlight.add(imageId);
        prefetchAttempts.set(imageId, (prefetchAttempts.get(imageId) ?? 0) + 1);
      }
      void loadImagesWithConcurrency(fresh, PREFETCH_CONCURRENCY)
        .then((results) => {
          const errors = new Map(
            results
              .filter((result) => result.error)
              .map((result) => [result.imageId, result.error]),
          );
          for (const imageId of fresh) {
            if (!errors.has(imageId)) {
              prefetchLoaded.add(imageId);
              continue;
            }
            prefetchRetryAt.set(imageId, Date.now() + PREFETCH_RETRY_MS);
            if (prefetchAttempts.get(imageId) === PREFETCH_MAX_ATTEMPTS)
              logger.warn(
                `[predictionMask] mask slice image failed to load ${PREFETCH_MAX_ATTEMPTS} times; not retrying: ${imageId.split("?")[0]}`,
                errors.get(imageId),
              );
          }
        })
        .catch((error) => {
          for (const imageId of fresh) prefetchRetryAt.set(imageId, Date.now() + PREFETCH_RETRY_MS);
          logger.warn("[predictionMask] mask slice prefetch failed", error);
        })
        .finally(() => {
          for (const imageId of fresh) prefetchInFlight.delete(imageId);
        });
    };

    if (!segmentationVolumeId) {
      // Nothing to draw into yet; keep publishing so the cards' wait can run out.
      const waitPoll = setInterval(() => {
        if (!publish()) clearInterval(waitPoll);
      }, 1000);
      if (!publish()) clearInterval(waitPoll);
      return () => {
        cancelled = true;
        clearInterval(waitPoll);
      };
    }

    const render = () => {
      if (cancelled) return;
      if (
        mask.segments.every(
          (segment) =>
            hiddenSegments.has(segment.segmentIndex) ||
            state.detachedSegmentIndices.has(segment.segmentIndex),
        )
      ) {
        clearInterval(readyPoll);
        publish();
        return;
      }
      attempts++;
      try {
        lastPass = addPredictionMaskContours(
          mask,
          segmentationVolumeId,
          state,
          (message, error) => logger.warn(message, error),
          hiddenSegments,
        );
        if (lastPass.added > 0) bumpContourVersion((version) => version + 1);
        prefetch(lastPass.pending);
      } catch (error) {
        if (attempts === READY_POLL_ATTEMPTS)
          logger.warn("[predictionMask] viewer was not ready after 10s", error);
      }
      stillLoading = publish() || !!lastPass?.pending.length;
      if (
        (!stillLoading && predictionMaskContoursArePresent(state)) ||
        attempts >= READY_POLL_ATTEMPTS
      )
        clearInterval(readyPoll);
    };
    let stillLoading = true;
    const readyPoll = setInterval(render, 200);
    const healthPoll = setInterval(() => {
      if (cancelled) return;
      if (predictionMaskContoursArePresent(state)) {
        // Draw slices whose header has streamed since the ready poll stopped.
        if (stillLoading) render();
        return;
      }
      // Cornerstone may clear global annotation state while switching between
      // drawing and review modes. Rebuild the immutable AI overlay rather than
      // leaving loadedKeys pointing at contours that no longer exist.
      removePredictionMaskContours(state);
      render();
    }, 1000);
    render();
    return () => {
      cancelled = true;
      clearInterval(readyPoll);
      clearInterval(healthPoll);
    };
  }, [
    studyId,
    mask,
    segmentationVolumeId,
    visibleSeriesKey,
    bumpContourVersion,
    hiddenSegments,
    releasedSegmentsKey,
  ]);

  return { beginPredictionEdit, getPredictionEditCompletion, maskLoadStatus, maskLoadDeadlines };
}
